import { useMemo } from 'react';
import { useTasksQuery } from 'shared/hooks/queries/use-tasks-query';
import {
  useRelationships,
  useRelationshipsMap,
} from 'shared/hooks/use-relationships';
import { useUser } from 'shared/hooks/use-user';
import { ID, IDPrefixes } from 'shared/types/id';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from 'shared/utils/prefix-id';

export type Options = {
  includeFilters?: boolean;
};

const defaultOptions: Options = {
  includeFilters: true,
};

export const useInboxTodos = ({ includeFilters } = defaultOptions) => {
  const user = useUser();
  const { data } = useTasksQuery(user?.uid, {
    uncompletedOnly: true,
    noGoalsOnly: true,
    noScheduleOnly: true,
  });
  const relationships = useRelationships();
  const relationshipsMap = useRelationshipsMap(relationships);

  return useMemo(() => {
    const tasks =
      data?.filter(
        ({
          goalId,
          lifeAreaId,
          archivedAt,
          completedAt,
          parentIds,
          endStrategy,
          deadlineDeadline,
        }) =>
          !goalId &&
          !lifeAreaId &&
          !archivedAt &&
          !completedAt &&
          !endStrategy?.deadline &&
          !deadlineDeadline &&
          !parentIds?.length,
      ) ?? [];

    if (!user?.settings?.inboxFilterByTags?.length || !includeFilters) {
      return tasks;
    }

    const tagTaskIds = user.settings.inboxFilterByTags
      .map((tagId) =>
        relationshipsMap[createPrefixId(tagId, IDPrefixes.Tag)]?.reduce<ID[]>(
          (acc, prefixedId) => {
            if (getPrefixedIdPrefix(prefixedId) === IDPrefixes.Task) {
              acc.push(getPrefixedId(prefixedId));
            }

            return acc;
          },
          [],
        ),
      )
      .flat(1);

    return tasks.filter(({ id }) => tagTaskIds.includes(id));
  }, [
    data,
    includeFilters,
    relationshipsMap,
    user?.settings?.inboxFilterByTags,
  ]);
};
